/** Minimize diagnostic payloads before they leave the service. */
export function scrubEvent<T extends object>(event: T, hint?: { attachments?: unknown }): T {
  if (hint) delete hint.attachments;
  const seen = new WeakSet<object>();
  const scrub = (value: unknown, depth = 0): unknown => {
    if (typeof value === "string")
      return value
        .replace(/https?:\/\/[^\s<>"']+/g, (raw) => {
          try {
            const u = new URL(raw);
            return `${u.origin}${u.pathname}`;
          } catch {
            return "[url]";
          }
        })
        .replace(/[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/gi, "[email]")
        .replace(/\bBearer\s+[^\s,;]+/gi, "Bearer [redacted]")
        .replace(/\b(?:sk_(?:live|test)_|whsec_|gh[pousr]_|github_pat_|sntrys_)[A-Za-z0-9_-]+/g, "[redacted]")
        .replace(/\b(?:password|passwd|secret|token|api[_-]?key)\s*[:=]\s*[^\s,;]+/gi, "[redacted]");
    if (!value || typeof value !== "object") return value;
    if (depth > 25 || seen.has(value)) return "[deep/circular]";
    seen.add(value);
    if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      if (
        /^(user|extra|breadcrumbs|request|contexts|attachments|headers|cookies|data|body|query_string|vars|pre_context|post_context|context_line)$/i.test(
          key
        )
      )
        continue;
      const safeKey = String(scrub(key));
      Object.defineProperty(out, safeKey, {
        value: /authorization|password|secret|token|api.?key|email|ip_address/i.test(key)
          ? "[redacted]"
          : scrub(item, depth + 1),
        enumerable: true,
        writable: true,
        configurable: true,
      });
    }
    return out;
  };
  return scrub(event) as T;
}

export function validDsn(dsn: string | undefined): boolean {
  if (!dsn) return false;
  try {
    const u = new URL(dsn);
    return (
      u.protocol === "https:" &&
      /^o\d+\.ingest(?:\.(?:us|de))?\.sentry\.io$/.test(u.hostname) &&
      !u.password &&
      !u.port &&
      /^\/\d+$/.test(u.pathname)
    );
  } catch {
    return false;
  }
}
